/**
 * Request History Section Component
 *
 * @module @features/requests/components/RequestDetails/components/RequestHistory
 */

import React from 'react';
import { useI18n, useRequestTranslations } from '@shared/i18n';
import { formatDate } from '@features/requests/utils';
import type { Request } from '@features/requests/types';
import styles from '../RequestDetails.module.css';

/**
 * Props for RequestHistory component
 */
export interface RequestHistoryProps {
    request: Request;
}

/**
 * Displays request history table
 */
export function RequestHistory({
    request,
}: RequestHistoryProps): React.ReactElement | null {
    const { t } = useI18n();
    const { getStatusLabel } = useRequestTranslations();

    if (!request.history || request.history.length === 0) return null;

    return (
        <div className={styles.section}>
            <h3 className={styles.sectionTitle}>
                {t('requests.details.history')}
            </h3>
            <table className={styles.historyTable}>
                <thead>
                    <tr>
                        <th className={styles.historyHeader}>
                            {t('requests.details.date')}
                        </th>
                        <th className={styles.historyHeader}>
                            {t('requests.details.from')}
                        </th>
                        <th className={styles.historyHeader}>
                            {t('requests.details.to')}
                        </th>
                        <th className={styles.historyHeader}>
                            {t('requests.details.changedBy')}
                        </th>
                        <th className={styles.historyHeader}>
                            {t('requests.details.note')}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {request.history.map((entry, index) => (
                        <tr
                            key={index}
                            className={styles.historyRow}
                        >
                            <td
                                className={styles.historyCell}
                                data-label={t('requests.details.date')}
                            >
                                {formatDate(entry.timestamp)}
                            </td>
                            <td
                                className={styles.historyCell}
                                data-label={t('requests.details.from')}
                            >
                                {getStatusLabel(entry.fromStatus)}
                            </td>
                            <td
                                className={styles.historyCell}
                                data-label={t('requests.details.to')}
                            >
                                {getStatusLabel(entry.toStatus)}
                            </td>
                            <td
                                className={styles.historyCell}
                                data-label={t('requests.details.changedBy')}
                            >
                                {entry.changedBy}
                            </td>
                            <td
                                className={styles.historyCell}
                                data-label={t('requests.details.note')}
                            >
                                {entry.notes ?? t('requests.details.noNote')}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
